/**
 * Persistent player progression - the ONE place XP becomes levels.
 *
 * KENOCYBER has **no design level cap**. Everything here is `bigint`
 * arithmetic end to end: no `number`, no `Math.pow`, no fractional exponent,
 * no rounding. A value that is exact when it is written stays exact when it is
 * read back years and several billion levels later.
 *
 * The client runs these functions for its local prediction and the server runs
 * them to decide, so a level-up can never be something the browser claims.
 */

/** Bumped when the persisted progression shape or curve changes. */
export const PROGRESSION_VERSION = 1;

/**
 * XP needed to go from level `level` to `level + 1`:
 *
 *     xpRequired(L) = 100 + 10L² + L³
 *
 * Cubic on purpose: it stays cheap for the first hours and grows fast enough
 * that an idle economy never runs out of curve.
 */
export function xpRequiredForLevel(level: bigint): bigint {
  assertPositiveLevel(level);
  return 100n + 10n * level * level + level * level * level;
}

/**
 * Total XP required to REACH the start of `level`, in closed form.
 *
 * With `n = level - 1` this is the sum of the curve over `1..n`:
 *
 *     100n + 10·n(n+1)(2n+1)/6 + [n(n+1)/2]²
 *
 * Closed form rather than a loop: levelling a player by a million levels must
 * cost the same as levelling them by one. Both divisions are exact in integer
 * arithmetic - `n(n+1)(2n+1)` is always divisible by 6 and `n(n+1)` by 2 - so
 * nothing is rounded away.
 */
export function totalXpBeforeLevel(level: bigint): bigint {
  assertPositiveLevel(level);
  const n = level - 1n;
  const squares = (10n * n * (n + 1n) * (2n * n + 1n)) / 6n;
  const triangular = (n * (n + 1n)) / 2n;
  return 100n * n + squares + triangular * triangular;
}

/**
 * Highest level whose entry cost is already paid by `lifetimeXp`.
 *
 * Exponential search for an upper bound, then binary search - O(log level).
 * `totalXpBeforeLevel` grows like n⁴/4, so even an absurd XP total needs only
 * a few dozen probes.
 */
export function levelForLifetimeXp(lifetimeXp: bigint): bigint {
  if (lifetimeXp < 0n) {
    throw new RangeError('lifetime xp must not be negative');
  }
  let high = 2n;
  while (totalXpBeforeLevel(high) <= lifetimeXp) {
    high *= 2n;
  }
  // Invariant: total(low) <= lifetimeXp < total(high).
  let low = high / 2n;
  while (high - low > 1n) {
    const mid = (low + high) / 2n;
    if (totalXpBeforeLevel(mid) <= lifetimeXp) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return low;
}

/**
 * Persistent progression.
 *
 * `lifetimeXp` is the source of truth; `level` and `xpIntoLevel` are always
 * derivable from it, and every write keeps that invariant:
 *
 *     lifetimeXp === totalXpBeforeLevel(level) + xpIntoLevel
 *     0 <= xpIntoLevel < xpRequiredForLevel(level)
 */
export interface ProgressionState {
  readonly level: bigint;
  /** XP accumulated toward the next level. */
  readonly xpIntoLevel: bigint;
  /** Every XP point ever earned. Never decreases. */
  readonly lifetimeXp: bigint;
  readonly freePoints: bigint;
  readonly damagePoints: bigint;
  readonly healthPoints: bigint;
  readonly attackSpeedPoints: bigint;
}

export const INITIAL_PROGRESSION: ProgressionState = Object.freeze({
  level: 1n,
  xpIntoLevel: 0n,
  lifetimeXp: 0n,
  freePoints: 0n,
  damagePoints: 0n,
  healthPoints: 0n,
  attackSpeedPoints: 0n,
});

export interface XpGrantResult {
  readonly state: ProgressionState;
  readonly levelsGained: bigint;
  /** One free point per level, always - never a fraction, never a bundle. */
  readonly pointsGranted: bigint;
}

/**
 * Applies an XP gain of any size.
 *
 * Adds to the lifetime total, re-derives the level by binary search and
 * computes the remainder exactly. A grant worth a million levels costs the
 * same handful of operations as a grant worth one.
 */
export function applyXpGain(state: ProgressionState, xpGain: bigint): XpGrantResult {
  if (xpGain < 0n) {
    throw new RangeError('xp gain must not be negative');
  }
  assertProgression(state);
  const lifetimeXp = state.lifetimeXp + xpGain;
  const level = levelForLifetimeXp(lifetimeXp);
  const levelsGained = level - state.level;
  return {
    state: {
      level,
      xpIntoLevel: lifetimeXp - totalXpBeforeLevel(level),
      lifetimeXp,
      freePoints: state.freePoints + levelsGained,
      damagePoints: state.damagePoints,
      healthPoints: state.healthPoints,
      attackSpeedPoints: state.attackSpeedPoints,
    },
    levelsGained,
    pointsGranted: levelsGained,
  };
}

/** Rebuilds a consistent state from a lifetime total alone. */
export function progressionFromLifetimeXp(
  lifetimeXp: bigint,
  points: {
    freePoints: bigint;
    damagePoints: bigint;
    healthPoints: bigint;
    attackSpeedPoints: bigint;
  },
): ProgressionState {
  const level = levelForLifetimeXp(lifetimeXp);
  return {
    level,
    xpIntoLevel: lifetimeXp - totalXpBeforeLevel(level),
    lifetimeXp,
    ...points,
  };
}

export interface PointAllocation {
  readonly damage: bigint;
  readonly health: bigint;
  readonly attackSpeed: bigint;
}

export type AllocationResult =
  | { readonly ok: true; readonly state: ProgressionState }
  | { readonly ok: false; readonly reason: 'invalid_amount' | 'insufficient_points' };

/**
 * Spends free points. The only ceiling is the balance the player owns - there
 * is no arbitrary per-attribute limit.
 */
export function allocatePoints(
  state: ProgressionState,
  allocation: PointAllocation,
): AllocationResult {
  const values = [allocation.damage, allocation.health, allocation.attackSpeed];
  if (values.some((value) => typeof value !== 'bigint' || value < 0n)) {
    return { ok: false, reason: 'invalid_amount' };
  }
  const total = values.reduce((sum, value) => sum + value, 0n);
  if (total === 0n) {
    return { ok: false, reason: 'invalid_amount' };
  }
  if (total > state.freePoints) {
    // Covers both "spending more than owned" and any attempt to go negative.
    return { ok: false, reason: 'insufficient_points' };
  }
  return {
    ok: true,
    state: {
      level: state.level,
      xpIntoLevel: state.xpIntoLevel,
      lifetimeXp: state.lifetimeXp,
      freePoints: state.freePoints - total,
      damagePoints: state.damagePoints + allocation.damage,
      healthPoints: state.healthPoints + allocation.health,
      attackSpeedPoints: state.attackSpeedPoints + allocation.attackSpeed,
    },
  };
}

/** XP still needed to reach the next level. */
export function xpToNextLevel(state: ProgressionState): bigint {
  return xpRequiredForLevel(state.level) - state.xpIntoLevel;
}

function assertPositiveLevel(level: bigint): void {
  if (typeof level !== 'bigint') {
    throw new TypeError('level must be a bigint');
  }
  if (level < 1n) {
    throw new RangeError('level must be at least 1');
  }
}

/** Fails loudly on any state that could not have been produced here. */
export function assertProgression(state: ProgressionState): void {
  assertPositiveLevel(state.level);
  const points = [
    state.xpIntoLevel,
    state.lifetimeXp,
    state.freePoints,
    state.damagePoints,
    state.healthPoints,
    state.attackSpeedPoints,
  ];
  if (points.some((value) => typeof value !== 'bigint' || value < 0n)) {
    throw new RangeError('progression values must be non-negative bigints');
  }
  if (state.xpIntoLevel >= xpRequiredForLevel(state.level)) {
    throw new RangeError('progression xpIntoLevel must be below the level requirement');
  }
  if (state.lifetimeXp !== totalXpBeforeLevel(state.level) + state.xpIntoLevel) {
    throw new RangeError('progression lifetimeXp does not match level and xpIntoLevel');
  }
}
